// Pure helpers for rendering the email shell HTML from a design config.
// Extracted so they can be unit-tested without the surrounding React page.

export type Align = "left" | "center" | "right";
export type Radius = "none" | "sm" | "md" | "lg";
export type SectionLayout = "logo" | "text" | "stacked" | "side_by_side";

export interface SectionConfig {
  logoFileId: number | null;
  logoName: string;
  logoHeightPx: number;
  text: string;
  layout: SectionLayout;
  align: Align;
}

export interface ShellDesignConfig {
  background: string;
  panel: string;
  text: string;
  buttonBg: string;
  buttonText: string;
  radius: Radius;
  contentAlign: Align;
  header: SectionConfig;
  footer: SectionConfig;
}

export const RADIUS_PX: Record<Radius, number> = { none: 0, sm: 6, md: 12, lg: 20 };
export const ALIGNMENTS: Align[] = ["left", "center", "right"];

export function escapeAttr(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

export function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

export function sanitiseLogoName(s: string): string {
  return (s || "").replace(/[^A-Za-z0-9._-]/g, "_").slice(0, 80);
}

export function clampInt(n: number, min: number, max: number): number {
  if (!Number.isFinite(n)) return min;
  return Math.max(min, Math.min(max, Math.round(n)));
}

const HEX_RE = /^#[0-9a-fA-F]{3,8}$/;
const RGB_RE = /^(?:rgb|rgba|hsl|hsla)\(\s*[\d.%\s,\/]+\)$/i;
const NAMED_RE = /^[a-zA-Z]{1,30}$/;
const TOKEN_ONLY_RE = /^\{\{\s*[A-Za-z_][\w.]*\s*\}\}$/;

/**
 * Allow only hex (#rgb / #rrggbb / #rrggbbaa), `rgb(a)?()`/`hsl(a)?()` numeric
 * triples, simple named-colour tokens (no separators, ≤30 chars), or a single
 * `{{Token}}` expression. Anything else falls back so admin-typed payloads
 * cannot break out of the `<style>` block.
 */
export function safeCssColor(value: string, fallback: string): string {
  const v = (value ?? "").trim();
  if (!v) return fallback;
  if (HEX_RE.test(v) || RGB_RE.test(v) || NAMED_RE.test(v) || TOKEN_ONLY_RE.test(v)) {
    return v;
  }
  return fallback;
}

export function buildSection(c: SectionConfig, kind: "header" | "footer"): string {
  const safeFileId = Number.isFinite(c.logoFileId) ? String(c.logoFileId) : "";
  const safeLogoName = sanitiseLogoName(c.logoName);
  const logoTag = safeFileId && safeLogoName
    ? `<img src="cid:${safeFileId}-${safeLogoName}" data-anythink-file-id="${safeFileId}" alt="${escapeAttr(safeLogoName || "Logo")}" style="max-height:${clampInt(c.logoHeightPx, 16, 240)}px; height:auto; max-width:100%; display:inline-block; vertical-align:middle;" />`
    : "";
  const textTag = c.text ? `<span class="${kind}-text">${escapeHtml(c.text)}</span>` : "";

  switch (c.layout) {
    case "logo":
      return logoTag || "";
    case "text":
      return textTag || "";
    case "stacked":
      return [logoTag, textTag].filter(Boolean).join("<br /><br />");
    case "side_by_side":
      return [logoTag, textTag].filter(Boolean).join(" &nbsp; ");
  }
}

export function buildShellHtml(c: ShellDesignConfig): string {
  const r = RADIUS_PX[c.radius];
  const headerMarkup = buildSection(c.header, "header");
  const footerMarkup = buildSection(c.footer, "footer");

  const background = safeCssColor(c.background, "#f6f7f9");
  const panel = safeCssColor(c.panel, "#ffffff");
  const text = safeCssColor(c.text, "#1f2937");
  const buttonBg = safeCssColor(c.buttonBg, "{{BrandColor}}");
  const buttonText = safeCssColor(c.buttonText, "#ffffff");
  const headerAlign = ALIGNMENTS.includes(c.header.align) ? c.header.align : "center";
  const contentAlign = ALIGNMENTS.includes(c.contentAlign) ? c.contentAlign : "left";
  const footerAlign = ALIGNMENTS.includes(c.footer.align) ? c.footer.align : "center";

  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>{{OrgName}}</title>
  <style>
    body { margin: 0; padding: 0; background: ${background}; font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; color: ${text}; }
    .wrapper { width: 100%; padding: 24px 0; }
    .panel { max-width: 560px; margin: 0 auto; background: ${panel}; border-radius: ${r}px; overflow: hidden; }
    .header { padding: 24px; text-align: ${headerAlign}; }
    .header-text { font-size: 18px; font-weight: 600; color: ${text}; }
    .content { padding: 0 24px 24px; text-align: ${contentAlign}; line-height: 1.55; }
    .footer { padding: 16px 24px; text-align: ${footerAlign}; font-size: 12px; color: ${text}; opacity: 0.7; }
    .footer-text { font-size: 12px; }
    a.button { display: inline-block; padding: 10px 18px; background: ${buttonBg}; color: ${buttonText}; text-decoration: none; border-radius: ${Math.min(r, 8)}px; font-weight: 600; }
    a.button:hover { opacity: 0.9; }
  </style>
</head>
<body>
  <div class="wrapper">
    <div class="panel">
      <div class="header">${headerMarkup}</div>
      <div class="content">
        {{Content}}
      </div>
      <div class="footer">${footerMarkup}</div>
    </div>
  </div>
</body>
</html>`;
}

